import { UserOrGroupsSchema } from 'src/schemas/global';
import { z } from 'zod';

export const AttestationTimeLimitSchema = z.enum([
  '1 day',
  '1 mon',
  '2 mons',
  '3 mons',
  '6 mons',
  '1 year',
  '2 years',
]);

const commonFields = z.object({
  attestationTimeLimit: AttestationTimeLimitSchema.nullish(),
  attestationPromptText: z.string().nullish(),
  requireReattestation: z.enum(['true', 'false']),
});

export const globalAttestationsSchema = commonFields.extend({
  requireAttestationFromEveryone: z.enum(['true']),
  // COI-670 — retained but dormant. While global attestation is on the
  // configured audience is not used to pick attesters, but it must survive
  // the round trip so toggling the flag back off restores it. This branch
  // therefore carries `attestationGroups` too: `zodResolver` hands
  // react-hook-form the PARSED output, so a field absent from the matched
  // union member is stripped before `onSave` ever sees it. No `.min(1)` here
  // — an empty audience is legitimate while global attestation is on.
  attestationGroups: UserOrGroupsSchema.optional().default([]),
});

export const groupAttestationsSchema = commonFields.extend({
  requireAttestationFromEveryone: z.enum(['false']),
  attestationGroups: UserOrGroupsSchema.min(1, {
    message:
      'You must select at least one group or user when attestation is not required from everyone',
  }),
});

export const AttestationFormSchema = z.discriminatedUnion(
  'requireAttestationFromEveryone',
  [globalAttestationsSchema, groupAttestationsSchema]
);

export type AttestationFormFieldData = z.infer<typeof AttestationFormSchema>;

export const defaultValues: AttestationFormFieldData = {
  attestationGroups: [],
  attestationPromptText: '',
  requireReattestation: 'true',
  attestationTimeLimit: null,
  requireAttestationFromEveryone: 'false',
};
